import { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { sanitizePayload } from "@/core/services/SanitizeService";
import { AxiosResponse, AxiosRequestConfig, AxiosError } from "axios";

// --- Refresh token queue state ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

const AUTH_ROUTES = [
  "/account/sign_in",
  "/account/refresh_token",
  "/account/sign_out",
  "/account/register",
  "/account/forgot_password",
  "/account/reset_password",
  "/account/confirm_email",
  "/account/resend_confirmation_email",
  "/account/verify_two_factor",
  "/account/setup_authenticator",
  "/account/verify_authenticator_setup",
];

const isAuthRoute = (url: string | undefined): boolean => {
  if (!url) return false;
  return AUTH_ROUTES.some((route) => url.includes(route));
};

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
    ApiService.vueInstance.axios.defaults.withCredentials = true;

    // Request interceptor: auto-attach token + sanitize payloads
    ApiService.vueInstance.axios.interceptors.request.use((config) => {
      const token = JwtService.getToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      config.headers["Accept"] = "application/json";

      if (
        config.data &&
        typeof config.data === "object" &&
        !(config.data instanceof FormData)
      ) {
        config.data = sanitizePayload(config.data);
      }
      return config;
    });

    // Response interceptor: 401 refresh with request queuing
    ApiService.vueInstance.axios.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        if (error.response?.status !== 401) {
          return Promise.reject(error);
        }

        // Do not attempt refresh for auth routes (prevents infinite loop)
        if (isAuthRoute(originalRequest?.url)) {
          return Promise.reject(error);
        }

        // Do not retry if we already retried this request
        if (originalRequest._retry) {
          ApiService.forceLogout();
          return Promise.reject(error);
        }

        // If a refresh is already in progress, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (token && originalRequest.headers) {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
              }
              return ApiService.vueInstance.axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await ApiService.refreshAccessToken();
          JwtService.saveToken(newToken);
          processQueue(null, newToken);
          if (originalRequest.headers) {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          }
          return ApiService.vueInstance.axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          ApiService.forceLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    );
  }

  /**
   * @description attempt to refresh the access token using the httpOnly refresh cookie
   */
  public static async refreshAccessToken(): Promise<string> {
    const response = await ApiService.vueInstance.axios.post(
      "/account/refresh_token",
      {}
    );
    const { accessToken } = response.data;
    if (!accessToken) {
      throw new Error("No access token returned from refresh endpoint");
    }
    return accessToken;
  }

  /**
   * @description force logout: clear tokens and redirect to sign-in
   */
  private static forceLogout(): void {
    const currentPath = window.location.pathname;
    if (currentPath !== "/sign-in") {
      JwtService.destroyAllTokens();
      window.location.href = "/sign-in";
    }
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  public static get(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(`${resource}`);
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param data: any
   * @param config: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, data, config);
  }

  /**
   * @description set the PATCH HTTP request
   * @param resource: string
   * @param data: any
   * @param config: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static patch(
    resource: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.patch(`${resource}`, data, config);
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource);
  }

  /**
   * @description Download a file as binary data
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static downloadFile(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, {
      responseType: "blob",
    });
  }
}

export default ApiService;
